import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { request } from '../lib/api';
import { rememberActivationReturn } from '../lib/activationReturn';
import { useSession } from '../session';
import './customer-public-entry.css';

const CARD_ID_PATTERN = /^[A-Za-z0-9_-]{32}$/;

type ClaimResponse = {
  card: {
    publicId: string;
    displayId: string;
    state: string;
    claimState: 'CLAIMED';
    claimedAt: string;
  };
  message: string;
};

function ActivateCardPage() {
  const { session } = useSession();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const scannedCardId = searchParams.get('card')?.trim() ?? '';
  const validScannedCardId = CARD_ID_PATTERN.test(scannedCardId) ? scannedCardId : '';
  const [cardPublicId, setCardPublicId] = useState(validScannedCardId);
  const [claimCode, setClaimCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [claimed, setClaimed] = useState<ClaimResponse | null>(null);

  const continueToAuth = (path: '/login' | '/create-account') => {
    if (validScannedCardId) rememberActivationReturn(validScannedCardId);
    navigate(path);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const response = await request<ClaimResponse>('/me/cards/claim', {
        method: 'POST',
        body: JSON.stringify({ cardPublicId: cardPublicId.trim(), claimCode: claimCode.trim() })
      });
      setClaimed(response);
    } catch (requestError: any) {
      setError(requestError?.data?.message ?? 'This card could not be activated. Check the code and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="portal-workspace card-activation-page">
      <header className="portal-intro">
        <span className="portal-eyebrow">Physical card activation</span>
        <h1>Connect this card to your Tap Page</h1>
        <p>The card shares no customer or organization information until an authorized recipient activates it.</p>
      </header>

      <section className="card-activation-panel">
        {claimed ? <div className="card-activation-success" role="status">
          <span aria-hidden="true">✓</span>
          <div><h2>Your card is active</h2><p>{claimed.message}</p><strong>{claimed.card.displayId}</strong></div>
          <Link className="button-primary" to="/card-design">Manage your card</Link>
        </div> : !session ? <div className="card-activation-auth">
          <div><h2>Sign in before entering the code</h2><p>This verifies that you belong to the workspace that owns the card. Scanning the card by itself is never enough to claim it.</p></div>
          <div className="card-activation-actions">
            <button type="button" className="button-primary" onClick={() => continueToAuth('/login')}>Sign in</button>
            <button type="button" className="button-secondary" onClick={() => continueToAuth('/create-account')}>Create account</button>
          </div>
        </div> : <form className="card-activation-form" onSubmit={handleSubmit}>
          <div><h2>Enter the separate activation code</h2><p>Use the code included with the card. It is different from the card ID and can only be used once.</p></div>
          <label>Card ID<input value={cardPublicId} onChange={(event) => setCardPublicId(event.target.value.trim())} pattern="[A-Za-z0-9_-]{32}" required readOnly={Boolean(validScannedCardId)} /></label>
          <label>Activation code<input value={claimCode} onChange={(event) => setClaimCode(event.target.value.toUpperCase())} autoComplete="one-time-code" minLength={32} maxLength={39} placeholder="XXXXXXXX-XXXXXXXX-XXXXXXXX-XXXXXXXX" required /></label>
          <p className="helper-text">Activating as {session.email}. The card will connect to your current InkTap workspace and Tap Page.</p>
          {error ? <p className="form-error" role="alert">{error}</p> : null}
          <button type="submit" className="button-primary" disabled={submitting}>{submitting ? 'Activating…' : 'Activate card'}</button>
        </form>}
      </section>
    </div>
  );
}

export default ActivateCardPage;
